// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductImage, { ProductImageStyles } from "@bg/Overrides/Components/ProductImage";
import { color } from "@bg/XDL/Utilities";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import Badge from "@insite/mobius/Badge";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showImage: boolean;
    showCompare: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductListProductImageStyles;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => ({
    settingsCollection: getSettingsCollection(state),
    productCompareChecked: getProductCompareChecked(state, props.productContext),
    isProductCompareFull: getIsProductCompareFull(state),
    view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
});

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasProductContext;

export interface ProductListProductImageStyles {
    // grid
    gridImageWrapper?: InjectableCss;
    gridImageContainer?: InjectableCss;
    gridImageActionsWrapper?: InjectableCss;
    gridImageActionsCompareWrapper?: InjectableCss;
    gridImageActionsListWrapper?: InjectableCss;
    gridProductImage?: ProductImageStyles;

    // list
    listImageWrapper?: InjectableCss;
    listProductImage?: ProductImageStyles;

    // shared
    compareCheckbox?: CheckboxPresentationProps;
    sponsoredFlag?: InjectableCss;

    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productImageStyles: ProductListProductImageStyles = {
    gridImageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    gridProductImage: {
        image: {
            width: "100%",
            height: "236px",
            cloudinaryWidth: "236",
            cloudinary: true,
            css: css`
                img {
                    max-width: 100%;
                }
            `,
        },
    },
    gridImageActionsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            min-height: 18px;
            margin-bottom: 3px;
        `,
    },

    listImageWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            flex-flow: column;
            overflow: hidden;
        `,
    },
    listProductImage: {
        image: {
            width: "139px",
            height: "139px",
            cloudinaryWidth: "139",
            cloudinary: true,
            css: css`
                img {
                    max-width: 100%;
                }
            `,
        },
    },
    sponsoredFlag: {
        css: css`
            font-size: 13px;
            min-height: 18px;
            font-style: italic;
            color: ${color("common.ui2")};
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 75%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const ProductListProductImage = ({
    productContext,
    showImage,
    showTextBadge,
    showImageBadge,
    showCompare,
    showAddToList,
    isProductCompareFull,
    settingsCollection,
    productCompareChecked,
    extendedStyles,
    addProductToCompare,
    removeProductFromCompare,
    view,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge,
        showImageBadge,
        styles,
    });

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    return (
        <>
            {view !== "List" && (
                <StyledWrapper
                    className="grid-imageWrapper"
                    data-test-selector="productListProductImage"
                    data-test-key={productContext.product.id}
                    {...styles.gridImageWrapper}
                >
                    <StyledWrapper {...styles.gridImageContainer}>
                        {showImage && !showOverlayBadge && (
                            <ProductImage extendedStyles={styles.gridProductImage} product={productContext} />
                        )}
                        {showImage && showOverlayBadge && overlayBadge && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productListBadge"
                            >
                                <ProductImage extendedStyles={styles.gridProductImage} product={productContext} />
                                <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.sponsoredFlag}>
                        {productContext.product.isSponsored && translate("Sponsored")}
                    </StyledWrapper>
                    <StyledWrapper {...styles.gridImageActionsWrapper}>
                        <StyledWrapper {...styles.gridImageActionsListWrapper}>
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector={`grid_actionsAddToList${productContext.product.id}`}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.gridImageActionsCompareWrapper}>
                            {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                                <Checkbox
                                    {...styles.compareCheckbox}
                                    checked={productCompareChecked}
                                    onChange={handleChanged}
                                    data-test-selector="productList_CompareCheckbox"
                                >
                                    {translate("Compare")}
                                </Checkbox>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {view === "List" && (
                <StyledWrapper
                    className="list-imageWrapper"
                    data-test-selector="productListProductImage"
                    data-test-key={productContext.product.id}
                    {...styles.listImageWrapper}
                >
                    {showImage && !showOverlayBadge && (
                        <ProductImage extendedStyles={styles.listProductImage} product={productContext} />
                    )}
                    {showImage && showOverlayBadge && overlayBadge && (
                        <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productListBadge">
                            <ProductImage extendedStyles={styles.listProductImage} product={productContext} />
                            <StyledWrapper {...overlayBadgeWrapperStyles}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.sponsoredFlag}>
                        {productContext.product.isSponsored && translate("Sponsored")}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListProductImage));
